/**
 * CCC Google reviews card (blocks/ccc-google-reviews.liquid).
 *
 * Google's REST Places API has no browser CORS, so we use the sanctioned
 * client-side path — the Maps JavaScript `Place` API. The library is loaded
 * lazily (only when a configured card scrolls near the viewport) so an
 * un-configured or off-screen cart never pulls Google's script.
 *
 * All Google-provided strings are written via textContent (never innerHTML) to
 * avoid injection. Reviews are shown in Google's own order with per-review links
 * back to Google + attribution, per the Maps Platform terms; nothing is cached.
 */

let mapsLoader = null;

/** Load the Google Maps JS API once (shared across every card on the page). */
function loadGoogleMaps(apiKey) {
  if (mapsLoader) return mapsLoader;
  mapsLoader = new Promise((resolve, reject) => {
    if (window.google && window.google.maps && window.google.maps.importLibrary) {
      resolve(window.google);
      return;
    }
    // Official Google Maps inline bootstrap loader (single-load, promise-based).
    ((g) => {
      let h;
      const c = 'google';
      const l = 'importLibrary';
      const q = '__ib__';
      const m = document;
      let b = window;
      b = b[c] || (b[c] = {});
      const d = b.maps || (b.maps = {});
      const r = new Set();
      const e = new URLSearchParams();
      const u = () =>
        h ||
        (h = new Promise((res, rej) => {
          const a = m.createElement('script');
          e.set('libraries', [...r] + '');
          for (const k in g) e.set(k.replace(/[A-Z]/g, (t) => '_' + t[0].toLowerCase()), g[k]);
          e.set('callback', c + '.maps.' + q);
          a.src = `https://maps.${c}apis.com/maps/api/js?` + e;
          d[q] = res;
          a.onerror = () => (h = rej(new Error('Google Maps could not load.')));
          a.nonce = m.querySelector('script[nonce]')?.nonce || '';
          m.head.append(a);
        }));
      d[l]
        ? console.warn('Google Maps JS API only loads once.')
        : (d[l] = (f, ...n) => r.add(f) && u().then(() => d[l](f, ...n)));
    })({ key: apiKey, v: 'weekly' });

    // Wait until importLibrary exists, then resolve.
    const start = performance.now();
    const tick = () => {
      if (window.google?.maps?.importLibrary) resolve(window.google);
      else if (performance.now() - start > 10000) reject(new Error('Google Maps load timed out.'));
      else requestAnimationFrame(tick);
    };
    tick();
  });
  return mapsLoader;
}

function starRow(rating) {
  const row = document.createElement('span');
  row.className = 'ccc-greviews__stars';
  row.setAttribute('aria-label', `${rating} out of 5 stars`);
  const rounded = Math.round(rating || 0);
  row.textContent = '★★★★★☆☆☆☆☆'.slice(5 - rounded, 10 - rounded);
  return row;
}

function reviewText(review) {
  // New Place API: review.text is a string; guard for the {text} object shape too.
  const t = review.text;
  if (!t) return '';
  return typeof t === 'string' ? t : t.text || '';
}

// Google returns reviews in relevance order with no "newest" option, so we sort
// the ones we got by publish date. review.publishTime is a Date (or ISO string).
function reviewTime(review) {
  if (!review.publishTime) return 0;
  const t = new Date(review.publishTime).getTime();
  return Number.isNaN(t) ? 0 : t;
}

class CCCGoogleReviews extends HTMLElement {
  connectedCallback() {
    if (this.dataset.ready) return;
    this.dataset.ready = '1';

    const key = this.dataset.apiKey;
    const placeId = this.dataset.placeId;
    if (!key || !placeId) return;

    // Defer the Google load until the card is close to view.
    if ('IntersectionObserver' in window) {
      const io = new IntersectionObserver(
        (entries) => {
          if (entries.some((e) => e.isIntersecting)) {
            io.disconnect();
            this.#render(key, placeId);
          }
        },
        { rootMargin: '400px' }
      );
      io.observe(this);
    } else {
      this.#render(key, placeId);
    }
  }

  async #render(key, placeId) {
    try {
      await loadGoogleMaps(key);
      const { Place } = await window.google.maps.importLibrary('places');
      const place = new Place({ id: placeId });
      await place.fetchFields({
        fields: ['reviews', 'rating', 'userRatingCount', 'googleMapsURI', 'displayName'],
      });

      const reviews = (place.reviews || []).filter((r) => reviewText(r).trim());
      if (!reviews.length) return; // nothing to show — stay hidden

      // Newest first among the (up to 5) reviews Google returns.
      reviews.sort((a, b) => reviewTime(b) - reviewTime(a));

      const max = Math.max(1, Math.min(5, parseInt(this.dataset.max, 10) || 5));
      const listingUrl = this.dataset.listingUrl || place.googleMapsURI || '';

      this.#paintSummary(place);
      this.#paintReviews(reviews.slice(0, max), place.googleMapsURI);
      this.#paintAll(listingUrl);

      this.hidden = false;
      this.#wireNav();
    } catch (err) {
      // Key/quota/network issue — leave the card hidden rather than show a broken box.
      console.warn('CCC Google reviews: unable to load', err);
    }
  }

  #paintSummary(place) {
    const el = this.querySelector('[data-greviews-summary]');
    if (!el || place.rating == null) return;
    const score = document.createElement('span');
    score.className = 'ccc-greviews__summary-score';
    score.textContent = Number(place.rating).toFixed(1);
    el.append(score);
    el.append(starRow(place.rating));
    if (place.userRatingCount) {
      const count = document.createElement('span');
      count.textContent = `(${place.userRatingCount.toLocaleString()} Google reviews)`;
      el.append(count);
    }
    el.hidden = false;
  }

  #paintReviews(reviews, placeUri) {
    const track = this.querySelector('[data-greviews-track]');
    if (!track) return;
    const frag = document.createDocumentFragment();

    reviews.forEach((review) => {
      const author = review.authorAttribution || {};
      const href = author.uri || placeUri || '';
      const card = document.createElement(href ? 'a' : 'div');
      card.className = 'ccc-greviews__card';
      card.setAttribute('role', 'listitem');
      if (href) {
        card.href = href;
        card.target = '_blank';
        card.rel = 'noopener noreferrer nofollow';
      }

      card.append(starRow(review.rating));

      const text = document.createElement('p');
      text.className = 'ccc-greviews__text';
      text.textContent = reviewText(review);
      card.append(text);

      const meta = document.createElement('div');
      meta.className = 'ccc-greviews__meta';
      if (author.photoURI) {
        const img = document.createElement('img');
        img.className = 'ccc-greviews__avatar';
        img.src = author.photoURI;
        img.alt = '';
        img.loading = 'lazy';
        img.width = 22;
        img.height = 22;
        meta.append(img);
      }
      const name = document.createElement('span');
      name.className = 'ccc-greviews__author';
      name.textContent = author.displayName || 'Google user';
      meta.append(name);
      if (review.relativePublishTimeDescription) {
        const when = document.createElement('span');
        when.textContent = review.relativePublishTimeDescription;
        meta.append(when);
      }
      card.append(meta);

      frag.append(card);
    });

    track.replaceChildren(frag);
  }

  #paintAll(url) {
    const link = this.querySelector('[data-greviews-all]');
    if (!link || !url) return;
    link.href = url;
    link.hidden = false;
  }

  // Arrow buttons replace the (hidden) scrollbar on fine-pointer devices; touch
  // scrolls the track directly. Shown only while the track actually overflows.
  #wireNav() {
    const track = this.querySelector('[data-greviews-track]');
    const prev = this.querySelector('[data-greviews-prev]');
    const next = this.querySelector('[data-greviews-next]');
    if (!track || !prev || !next) return;

    const page = () => Math.max(track.clientWidth * 0.9, 240);
    prev.addEventListener('click', () => track.scrollBy({ left: -page(), behavior: 'smooth' }));
    next.addEventListener('click', () => track.scrollBy({ left: page(), behavior: 'smooth' }));

    const update = () => {
      const maxScroll = track.scrollWidth - track.clientWidth;
      const canScroll = maxScroll > 1;
      prev.hidden = next.hidden = !canScroll;
      prev.disabled = track.scrollLeft <= 1;
      next.disabled = track.scrollLeft >= maxScroll - 1;
    };
    track.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    update();
  }
}

if (!customElements.get('ccc-google-reviews')) {
  customElements.define('ccc-google-reviews', CCCGoogleReviews);
}
